import React, { useEffect, useRef, useState } from "react";
import { Tabs, Tab } from "react-bootstrap";
import "react-quill/dist/quill.snow.css";
import "./CreateGroupModal.css";
import nodeService from "../../services/nodeService";
import IframeModal from "./IframeModal";
import InfoTab from "./InfoTab";
import LinkTab from "./LinkTab";
import ViewerTab from "./ViewerTab";
import { useDispatch, useSelector } from "react-redux";
import api from "../../api/apiClient";
import { Rnd } from "react-rnd";
import { toast } from "react-toastify";
import {
  fetchFilteredGraphData,
  pinNode,
  removeExpandedNode,
  setFetchedNodesData,
  unpinNode,
} from "../../redux/slices/graphSlice";
import { useUserId } from "../../Utils/helpers";
import { baseURL } from "../../env/env";

const SchemaInfo = ({
  show,
  handleClose,
  data,
  fetchFurtherPlaybookRules,
  cy,
  nodeSchema,
  isPlaybookLink,
  isPlaybook,
  fetchedData,
  cyRef,
  setNewEdge,
  setNewNode,
  isTopic,
  fetchTopicNodesCount,
  newEdge,
  newNode,
}) => {
  const { REACT_APP_API_ENDPOINT } = process.env;
  const [file, setFile] = useState(null);
  const [editorData, setEditorData] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [nodeProps, setNodeProps] = useState(data.props || {});
  const [originalNodeProps, setOriginalNodeProps] = useState(null);
  const [docxUrl, setDocxUrl] = useState(null);
  const [activeKey, setActiveKey] = useState(isPlaybook ? "viewer" : "info");
  const [playbookLink, setPlayBookLink] = useState("");
  const [currentFurtherRule, setCurrentFurtherRule] = useState(null);
  const [playbookHistory, setPlaybookHistory] = useState([]);
  const [attachmentNodes, setAttachmentNodes] = useState([]);
  const [expandedNodes, setExpandedNodes] = useState([]);
  const [isStartAgain, setStartAgain] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [filepath, setFilepath] = useState(null);
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [fileExists, setFileExists] = useState(null);
  const [size, setSize] = useState({
    width: isTopic ? windowWidth * 0.38 : windowWidth * 0.42,
    height: windowHeight - 120,
  });

  const [position, setPosition] = useState({
    x: isTopic ? 0 : windowWidth * 0.345,
    y: 0,
  });

  const { graphData, selectedLeafClasses, fetchedNodesData, selectedSearchId } =
    useSelector((state) => state.graph);

  const { edges = [], nodes = [] } = graphData ?? {};
  const dispatch = useDispatch();
  const userId = useUserId();
  const baseUrl = window.location.origin;
  const schemaNodesRef = useRef(new Set());
  const nodeSchemaRef = useRef(nodeSchema);

  const isViewerDisabled =
    data?.relations?.length > 0 &&
    data.relations.every((node) => node.relatedNodeName === "Unknown") &&
    data?.relatedPlaybookRules?.length === 0;

  // Function to handle the resize event
  const handleResize = () => {
    setWindowWidth(window.innerWidth);
    setWindowHeight(window.innerHeight);
  };

  const fetchNode = (edge) => {
    if (!edge || !edge.target) return [];
    return newNode
      ? newNode.find((node) => node.id === edge.target)
      : fetchedData
      ? fetchedData?.nodes.find((node) => node.id === edge.target)
      : nodes.find((node) => node.id === edge.target);
  };

  const getAttachmentNodes = (rule) => {
    const attachmentEdges = edges.filter(
      (edge) => edge.source === rule.id && edge.class === "HasAttachment"
    );
    return attachmentEdges
      .filter((edge) => fetchNode(edge) !== undefined)
      .map((edge) => fetchNode(edge));
  };

  // Update the size based on the window width and zoom level (devicePixelRatio)
  useEffect(() => {
    const zoomLevel = window.devicePixelRatio; // Capture the zoom level
    const newWidth = windowWidth * 0.4 * zoomLevel; // Update width based on zoom level

    setSize({
      width: newWidth,
      height: windowHeight - 100, // Keep the height fixed
    });
  }, [windowWidth, windowHeight]);

  // Add event listener for window resize
  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    setActiveKey(isPlaybook ? "viewer" : "info");
  }, [isPlaybook]);

  useEffect(() => {
    if (data) {
      setPlayBookLink(
        `${baseUrl}/knowledge/#playbook_openOid=${data.props?._oid}&filter_includeManual=${data.props?._oid}&select=${data.props?._oid}&v=1`
      );
    }
  }, [data]);

  useEffect(() => {
    if (currentFurtherRule) {
      setAttachmentNodes(getAttachmentNodes(currentFurtherRule));
    }
  }, [currentFurtherRule]);

  useEffect(() => {
    if (data) {
      setIsEditing(false);

      if (data.props?.attachment) {
        fetchDocumentContent(data.props?.attachment);
      }
      // setActiveKey(data.class === "Playbook" ? "viewer" : "info");
      setActiveKey(
        (selectedLeafClasses?.length > 0 || isPlaybook) &&
          data.class === "Playbook"
          ? "viewer"
          : "info"
      );
    }
  }, [data]);

  useEffect(() => {
    setNodeProps(data.props || {});
  }, [data]);

  useEffect(() => {
    if (currentFurtherRule && !isPlaybookLink) {
      if (!nodeSchema) return;
      const targetNode = nodeSchema.getElementById(
        String(currentFurtherRule.id)
      );
      if (targetNode.length > 0) {
        // targetNode.style({
        //   "border-width": "5px",
        //   "border-color": "red",
        //   "border-style": "solid",
        //   "background-opacity": 0.8,
        // });
        // targetNode.ungrabify();
        // if (!fetchedPinnedNodes.has(targetNode.id())) {
        dispatch(pinNode(targetNode.id()));
        // }
      } else {
        // Create the currentFurtherRule node
        const currentNode = {
          data: {
            id: currentFurtherRule.id,
            label: currentFurtherRule.display,
            color: currentFurtherRule.colour || "#666",
          },
        };

        // Check if the node already exists
        const existingIds = new Set(
          nodeSchema.nodes().map((node) => node.id())
        );

        // If the node is not already in the graph, add it
        if (!existingIds.has(currentFurtherRule.id)) {
          // Add the current node
          nodeSchema.add([currentNode]);

          // Filter edges that connect the newly added currentFurtherRule with already expanded nodes
          const relevantEdges = edges.filter(
            (edge) =>
              ((edge.source === currentFurtherRule.id &&
                expandedNodes.some((node) => node.id === edge.target)) ||
                (edge.target === currentFurtherRule.id &&
                  expandedNodes.some((node) => node.id === edge.source))) &&
              // Ensure both source and target nodes exist in the graph
              nodeSchema.getElementById(edge.source).length > 0 &&
              nodeSchema.getElementById(edge.target).length > 0
          );

          // Map the filtered edges into the required format
          const newEdges = relevantEdges.map((edge) => ({
            data: {
              id: edge.id,
              source: edge.source,
              target: edge.target,
              label:
                edge.class === "IsAnswerFor" && edge.props.value
                  ? `${edge.display} = ${edge.props.value}`
                  : edge.display || "",
            },
          }));

          // Add the filtered edges to the graph
          nodeSchema.add(newEdges);
          dispatch(pinNode(currentNode.data.id));
          // dispatch(addExpandedNode(currentNode.data.id));

          // Run the layout to adjust the graph structure

          // nodeSchema?.layout(defaultLayout)?.run();

          // Apply styling to the newly added node
          const nodeEle = nodeSchema.getElementById(currentFurtherRule.id);
          nodeEle.style({
            "border-width": 5,
            "border-color": "red",
            "border-style": "double",
            // "text-outline-width": 2,
          });

          // Update expanded nodes to include the new currentFurtherRule
          setExpandedNodes((prev) => [...prev, currentFurtherRule]);
        }
      }
    }
  }, [currentFurtherRule, nodeSchema, isPlaybookLink]);

  useEffect(() => {
    if (expandedNodes.length > 0) {
      if (playbookHistory.length > 0) {
        expandedNodes.forEach((node) => {
          const nodeElement = nodeSchema.getElementById(node.id);
          if (
            nodeElement &&
            nodeElement.length > 0 &&
            !playbookHistory.some((rule) => {
              return rule.id === parseInt(node.id);
            })
          ) {
            nodeSchema.remove(nodeElement);
            dispatch(unpinNode(nodeElement.id()));
            dispatch(removeExpandedNode(nodeElement.id()));
          }
        });
      } else if (isStartAgain) {
        expandedNodes.forEach((node) => {
          const nodeElement = nodeSchema.getElementById(node.id);
          if (nodeElement && nodeElement.length > 0) {
            nodeSchema.remove(nodeElement);
            dispatch(unpinNode(nodeElement.id()));
            dispatch(removeExpandedNode(nodeElement.id()));
          }
        });
        setExpandedNodes([]);
        setStartAgain(false);
      }
    }
  }, [expandedNodes, isStartAgain, nodeSchema, playbookHistory]);

  const handlePreview = (value) => {
    const fileExtension = value.split(".").pop().toLowerCase();

    if (fileExtension === "docx") {
      // Encode the base URL with the file path
      const encodedUrl = encodeURIComponent(
        `${baseURL}/uploads/${encodeURIComponent(value)}`
      );

      const officeUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`;
      setDocxUrl(officeUrl);
    } else {
      window.open(`${baseURL}/uploads/${encodeURIComponent(value)}`);
    }
  };

  function extractFilename(value) {
    if (value) {
      const lastSlashIndex = value.lastIndexOf("/");
      const lastBackslashIndex = value.lastIndexOf("\\");

      // If the value contains slashes or backslashes, extract the filename
      if (lastSlashIndex !== -1 || lastBackslashIndex !== -1) {
        const separatorIndex = Math.max(lastSlashIndex, lastBackslashIndex);
        value = value.slice(separatorIndex + 1); // Extract the filename
      }

      return value;
    }
  }

  const checkIfFileExists = async (filename) => {
    try {
      if (!filename) return;

      setIsFileLoading(true); // Start loader

      const decoded_filename = extractFilename(filename);
      const response = await api.post("/document/check-file", {
        filename: decoded_filename,
      });

      if (response && response.data.res === false) {
        setFileExists(false); // File does not exist
      } else {
        setFileExists(true); // File exists
        return true;
      }
    } catch (error) {
      console.error("Error while checking file existence:", error);
    } finally {
      setIsFileLoading(false); // Stop loader
    }
  };

  const fetchDocumentContent = async (filePath) => {
    try {
      const result = await nodeService.fetchDocumentContent(
        extractFilename(filePath)
      );
      setEditorData(result.FileContent);
    } catch (error) {
      console.error("Error fetching document content:", error);
    }
  };

  const handleDeleteRelation = async (relationId) => {
    try {
      // await api.delete(`/relations/${relationId}/`);
      const response = await fetch(
        `${REACT_APP_API_ENDPOINT}/relations/${relationId}/`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": "6XxnCdOnCfwHEKwM",
          },
        }
      );
      if (response.status === 204) {
        const edge = [
          ...edges,
          ...(fetchedNodesData.edges ?? []),
          ...(newEdge ?? []),
        ].find((edge) => edge.id === relationId);
        const sourceNode = [
          ...nodes,
          ...(fetchedNodesData.nodes ?? []),
          ...(newNode ?? []),
        ].find((node) => node.id === edge?.source);
        await fetchTopicNodesCount(sourceNode?.id);
        toast.success("Instance deleted successfully");
        dispatch(fetchFilteredGraphData({ userId: userId }));
      } else {
        toast.error("Failed to delete instance");
      }
    } catch (error) {
      console.error("Failed to delete instance", error);
      toast.error("Failed to delete instance");
    }
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0]; // Get the first selected file
    if (selectedFile) {
      setFile(selectedFile); // Store the selected file in state
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("No file selected for upload.");
      return;
    }

    const checkPayload = {
      filename: extractFilename(file.name),
    };

    try {
      // Check if the file already exists
      const checkResponse = await api.post(
        `/document/check-file`,
        checkPayload
      );

      if (checkResponse && checkResponse.data.res === false) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("name", extractFilename(file.name));
        formData.append("size", file.size);
        formData.append("type", file.type);

        const uploadResponse = await api.post(`/document/`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        if (uploadResponse.status === 200) {
          setNodeProps((prevProps) => ({
            ...prevProps,
            attachment: uploadResponse.data.filepath || file.name,
          }));
          setFilepath(uploadResponse.data.filepath ?? null);
          toast.success("File uploaded successfully.");
        }
      } else {
        toast.error("Filename already exist.");
      }
    } catch (error) {
      toast.error(
        error.response.data.error || "File upload failed. Please try again."
      );
      console.error("File upload error:", error);
    }
  };

  const handleEditClick = () => {
    setOriginalNodeProps(nodeProps);
    setIsEditing(true);
  };

  const handleInputChange = (key, value) => {
    setNodeProps((prevProps) => ({
      ...prevProps,
      [key]: value,
    }));
  };

  const handleSaveClick = async () => {
    const nodeId = data.id;

    if (!nodeProps.name || nodeProps.name.trim() === "") {
      toast.error("Name cannot be empty");
      return;
    }

    let updatedNodeProps = { ...nodeProps }; // Create a shallow copy

    if (
      typeof nodeProps.keywords === "string" &&
      nodeProps.keywords.trim() !== ""
    ) {
      if (nodeProps.keywords.endsWith(",")) {
        updatedNodeProps.keywords = nodeProps.keywords.slice(0, -1).trim();
      }

      updatedNodeProps.keywords = updatedNodeProps.keywords.split(", ");
    }

    try {
      const response = await api.post(
        `/instances/${nodeId}/`,
        updatedNodeProps,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      if (response.status === 200) {
        const updatedData = response.data.new_props;
        const fetchedNodes = fetchedNodesData.nodes.map((node) =>
          node.id === nodeId
            ? {
                ...node,
                props: updatedData,
                display: `${updatedData.name} (:${node.class})`,
              }
            : node
        );
        dispatch(
          setFetchedNodesData({
            nodes: fetchedNodes,
            edges: fetchedNodesData.edges,
          })
        );

        setNodeProps(updatedData);
        if (!selectedSearchId) {
          dispatch(fetchFilteredGraphData({ userId: userId }));
        }
        toast.success("Node information updated successfully");
        setIsEditing(false);
      }
    } catch (error) {
      console.error("Error updating node information:", error);
      toast.error("Failed to update node information");
    }
  };

  const handleCancelClick = () => {
    setNodeProps(originalNodeProps);
    setIsEditing(false);
  };

  useEffect(() => {
    nodeSchemaRef.current = nodeSchema;
  }, [nodeSchema]);

  useEffect(() => {
    const currentNodes = new Set(expandedNodes.map((node) => node.id));
    schemaNodesRef.current = currentNodes;
  }, [expandedNodes]);

  useEffect(() => {
    return () => {
      if (!nodeSchemaRef.current) return;

      const nodesToRemove = Array.from(schemaNodesRef.current);

      nodesToRemove.forEach((nodeId) => {
        const nodeElement = nodeSchemaRef.current.getElementById(nodeId);
        if (nodeElement && nodeElement.length > 0) {
          nodeSchemaRef.current.remove(nodeElement);
          dispatch(unpinNode(nodeId));
          dispatch(removeExpandedNode(nodeId));
        }
      });

      schemaNodesRef.current.clear();
    };
  }, []);

  const renderTabs = () => {
    const tabs = [
      <Tab
        eventKey="info"
        title={
          <span
            className={activeKey === "info" ? "active-tab" : "inactive-tab"}
          >
            Info
          </span>
        }
        key="info"
      />,
    ];

    if (data.class === "Playbook") {
      tabs.push(
        <Tab
          eventKey="viewer"
          title={
            <span
              className={activeKey === "viewer" ? "active-tab" : "inactive-tab"}
            >
              Open Playbook Viewer
            </span>
          }
          key="viewer"
          disabled={isViewerDisabled}
        />,
        <Tab
          eventKey="link"
          title={
            <span
              className={activeKey === "link" ? "active-tab" : "inactive-tab"}
            >
              Link Playbook
            </span>
          }
          key="link"
        />
      );
    }

    return tabs;
  };

  if (isPlaybookLink) {
    return (
      <>
        <div className="full-screen-view">
          <ViewerTab
            data={data}
            playbookHistory={playbookHistory}
            setPlaybookHistory={setPlaybookHistory}
            setCurrentFurtherRule={setCurrentFurtherRule}
            fetchNode={fetchNode}
            attachmentNodes={attachmentNodes}
            fetchDocumentContent={fetchDocumentContent}
            setStartAgain={setStartAgain}
            getAttachmentNodes={getAttachmentNodes}
            setAttachmentNodes={setAttachmentNodes}
            extractFilename={extractFilename}
            newNode={newNode}
            newEdge={newEdge}
            expandedNodes={expandedNodes}
            handlePreview={handlePreview}
            checkIfFileExists={checkIfFileExists}
          />
        </div>
        {docxUrl && (
          // <div className="full-screen-view">
          <IframeModal
            show={!!docxUrl}
            onClose={() => setDocxUrl(null)}
            url={docxUrl}
            isPlaybookLink={isPlaybookLink}
          />
          // </div>
        )}
      </>
    );
  }

  const renderContent = () => {
    if (activeKey === "info") {
      return (
        <InfoTab
          data={data}
          handleCancelClick={handleCancelClick}
          handleDeleteRelation={handleDeleteRelation}
          handlePreview={handlePreview}
          handleEditClick={handleEditClick}
          handleSaveClick={handleSaveClick}
          isEditing={isEditing}
          editorData={editorData}
          nodeProps={nodeProps}
          handleInputChange={handleInputChange}
          handleFileChange={handleFileChange}
          handleUpload={handleUpload}
          cyschema={cy}
          cyRef={cyRef}
          setNewEdge={setNewEdge}
          setNewNode={setNewNode}
          fetchTopicNodesCount={fetchTopicNodesCount}
          extractFilename={extractFilename}
          setNodeProps={setNodeProps}
          selectedSearchId={selectedSearchId}
          filePath={filepath}
          setFilepath={setFilepath}
          isFileLoading={isFileLoading}
          fileExists={fileExists}
          setFileExists={setFileExists}
          checkIfFileExists={checkIfFileExists}
        />
      );
    } else if (activeKey === "link") {
      return <LinkTab playbookLink={playbookLink} />;
    } else if (activeKey === "viewer") {
      return (
        <ViewerTab
          data={data}
          playbookHistory={playbookHistory}
          setPlaybookHistory={setPlaybookHistory}
          setCurrentFurtherRule={setCurrentFurtherRule}
          fetchNode={fetchNode}
          attachmentNodes={attachmentNodes}
          fetchDocumentContent={fetchDocumentContent}
          setStartAgain={setStartAgain}
          getAttachmentNodes={getAttachmentNodes}
          setAttachmentNodes={setAttachmentNodes}
          extractFilename={extractFilename}
          newNode={newNode}
          newEdge={newEdge}
          expandedNodes={expandedNodes}
          handlePreview={handlePreview}
          checkIfFileExists={checkIfFileExists}
        />
      );
    }
  };

  return (
    <>
      <Rnd
        // size={size}
        position={{
          x: position.x,
          y: position.y,
        }} // Add this to ensure the position is used
        default={{
          x: window.innerWidth - size.width,
          y: 40,
          width: size.width,
          height: size.height,
        }}
        onDragStop={(e, d) => setPosition({ x: d.x, y: d.y })}
        onResize={(e, direction, ref, delta, position) => {
          setSize({
            width: ref.offsetWidth,
            height: ref.offsetHeight,
          });
          setPosition(position);
        }}
        style={{
          display: show ? "flex" : "none",
          flexDirection: "column",
          background: "white",
          boxShadow: "0px 0px 10px rgba(0,0,0,0.1)",
          border: "1px solid #ddd",
          zIndex: 1000,
          position: "fixed",
        }}
        bounds="window"
        dragHandleClassName="drag-panel"
        width={660}
        minWidth={450}
        disableDragging={isTopic}
      >
        <div
          className={`overflow-hidden ${show ? "open" : ""}`}
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            className="tab-header d-flex flex-column flex-grow-1"
            style={{
              overflowY: "auto",
              height: parseInt(windowHeight) - 120,
            }}
          >
            <div className="tabs-container drag-panel dragger">
              <Tabs
                activeKey={activeKey}
                onSelect={(k) => setActiveKey(k)}
                style={{
                  cursor: isViewerDisabled ? "not-allowed" : "pointer",
                }}
              >
                {renderTabs()}
              </Tabs>
              <button className="closee-btn" onClick={handleClose}>
                &times;
              </button>
            </div>
            {renderContent()}
          </div>
          {/* <div className="side-panel-footer">
          <Button
            variant="secondary"
            className="cance-button"
            onClick={handleClose}
          >
            Cancel
          </Button>
        </div> */}
        </div>
      </Rnd>
      {/* Render Iframe Modal for docx files */}
      <IframeModal
        show={!!docxUrl}
        onClose={() => setDocxUrl(null)}
        url={docxUrl}
        isPlaybookLink={isPlaybookLink}
      />
    </>
  );
};

export default SchemaInfo;
